"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Fade,
  Zoom,
  IconButton,
  InputAdornment,
} from "@mui/material"
import { styled, keyframes } from "@mui/system"
import PersonIcon from "@mui/icons-material/Person"
import PhoneIcon from "@mui/icons-material/Phone"
import SportsEsportsIcon from "@mui/icons-material/SportsEsports"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CancelIcon from "@mui/icons-material/Cancel"
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents"
import StarIcon from "@mui/icons-material/Star"
import ExitToAppIcon from "@mui/icons-material/ExitToApp"
import GetAppIcon from "@mui/icons-material/GetApp"
import io from "socket.io-client"
import { API_URL, WS_URL } from "./config"

// ============ אנימציות ============
const pulseAnimation = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(33,150,243,.7); }
  70% { transform: scale(1.06); box-shadow: 0 0 0 14px rgba(33,150,243,0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(33,150,243,0); }
`
const glowAnimation = keyframes`
  0%,100% { text-shadow: 0 0 8px rgba(255,255,255,.3), 0 0 16px rgba(33,150,243,.25); }
  50% { text-shadow: 0 0 16px rgba(255,255,255,.6), 0 0 32px rgba(33,150,243,.45); }
`
const floatAnimation = keyframes`
  0%,100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`
const sparkleAnimation = keyframes`
  0%,100% { opacity: 0; transform: scale(0); }
  50% { opacity: 1; transform: scale(1); }
`
const correctFlash = keyframes`
  0% { background: rgba(76,175,80,.15); }
  50% { background: rgba(76,175,80,.5); }
  100% { background: rgba(76,175,80,.15); }
`
const wrongFlash = keyframes`
  0% { background: rgba(244,67,54,.15); }
  50% { background: rgba(244,67,54,.5); }
  100% { background: rgba(244,67,54,.15); }
`

// ============ סטיילד קומפוננטות ============
const MobileContainer = styled(Box)({
  minHeight: "100vh",
  width: "100vw",
  background: "linear-gradient(135deg, #0c0c0c 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #533483 100%)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "hidden",
  padding: "1rem",
  boxSizing: "border-box",
  userSelect: "none",
  WebkitTapHighlightColor: "transparent",
})

const GameCard = styled(Box)({
  background: "rgba(255,255,255,.08)",
  backdropFilter: "blur(18px)",
  border: "2px solid rgba(33,150,243,.3)",
  borderRadius: "28px",
  padding: "1.5rem",
  width: "100%",
  maxWidth: "420px",
  boxShadow: "0 12px 48px rgba(0,0,0,.35)",
})

const AnswerButton = styled(Button)(({ answered, correct, wrong }) => ({
  width: "100%",
  padding: "18px 16px",
  borderRadius: "18px",
  fontSize: "1.3rem",
  fontWeight: 700,
  marginBottom: "12px",
  border: "2px solid rgba(255,255,255,.2)",
  color: "#fff",
  background: answered
    ? correct
      ? "linear-gradient(135deg, #4caf50, #66bb6a)"
      : wrong
        ? "linear-gradient(135deg, #f44336, #ef5350)"
        : "rgba(255,255,255,.06)"
    : "rgba(255,255,255,.06)",
  backdropFilter: "blur(8px)",
  textTransform: "none",
  transition: "all .2s ease",
  "&:hover": {
    background: answered ? undefined : "rgba(33,150,243,.25)",
    borderColor: "rgba(33,150,243,.6)",
    transform: "scale(1.02)",
  },
  "&:active": {
    transform: "scale(.97)",
  },
  "&:disabled": {
    opacity: 0.65,
    color: "#fff",
  },
}))

const StyledInput = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,.1)",
    backdropFilter: "blur(10px)",
    borderRadius: "15px",
    color: "#fff",
    "& fieldset": { borderColor: "rgba(255,255,255,.3)", borderWidth: "2px" },
    "&:hover fieldset": { borderColor: "rgba(33,150,243,.5)" },
    "&.Mui-focused fieldset": { borderColor: "#2196f3", boxShadow: "0 0 10px rgba(33,150,243,.3)" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,.7)", "&.Mui-focused": { color: "#2196f3" } },
  "& .MuiInputBase-input": { color: "#fff" },
})

const ScoreChip = styled(Box)({
  background: "linear-gradient(135deg, #ffd700, #ff9800)",
  borderRadius: "50px",
  padding: "8px 22px",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  color: "#1a1a2e",
  fontWeight: 700,
  fontSize: "1.1rem",
  boxShadow: "0 4px 16px rgba(255,215,0,.35)",
})

// כוכבים מנצנצים
const SparkleEffect = () => (
  <>
    {[...Array(12)].map((_, i) => (
      <StarIcon
        key={i}
        sx={{
          position: "absolute",
          color: "#ffd700",
          fontSize: Math.random() * 16 + 8,
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          animation: `${sparkleAnimation} ${2 + Math.random() * 3}s infinite`,
          animationDelay: `${Math.random() * 2}s`,
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
    ))}
  </>
)

// ============ קומפוננטה ראשית ============
const MobilePlay = () => {
  const { gameId } = useParams()
  const [enteredCode, setEnteredCode] = useState("")
  const [codeError, setCodeError] = useState("")

  // אם אין gameId בURL — מסך הכנסת קוד
  if (!gameId) {
    return <MobileCodeEntry enteredCode={enteredCode} setEnteredCode={setEnteredCode} codeError={codeError} setCodeError={setCodeError} />
  }

  return <MobileGame gameId={gameId} />
}

// ============ מסך הכנסת קוד משחק ============
const MobileCodeEntry = ({ enteredCode, setEnteredCode, codeError, setCodeError }) => {
  const navigate = useNavigate()

  const handleGoToGame = () => {
    if (!enteredCode.trim()) {
      setCodeError("הכנס קוד משחק")
      return
    }
    setCodeError("")
    navigate(`/m/${enteredCode.trim()}`)
  }

  return (
    <MobileContainer>
      <SparkleEffect />
      <Zoom in timeout={600}>
        <GameCard>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <SportsEsportsIcon sx={{ fontSize: "4rem", color: "#2196f3", filter: "drop-shadow(0 0 12px rgba(33,150,243,.7))" }} />
            <Typography
              variant="h4"
              sx={{
                color: "#fff",
                fontWeight: 700,
                mt: 1.5,
                background: "linear-gradient(45deg, #2196f3, #21cbf3)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              קליקרים
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,.5)", mt: 1, fontSize: "1rem" }}>
              הכנס את קוד המשחק כדי להתחיל
            </Typography>
          </Box>

          {codeError && (
            <Fade in>
              <Box sx={{ background: "rgba(244,67,54,.2)", borderRadius: "12px", p: 1.5, mb: 2, textAlign: "center" }}>
                <Typography sx={{ color: "#ff5252", fontWeight: 600 }}>{codeError}</Typography>
              </Box>
            </Fade>
          )}

          <StyledInput
            fullWidth
            label="קוד משחק"
            variant="outlined"
            margin="normal"
            value={enteredCode}
            onChange={(e) => setEnteredCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleGoToGame()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SportsEsportsIcon sx={{ color: "rgba(255,255,255,.7)" }} />
                </InputAdornment>
              ),
            }}
            sx={{ direction: "ltr", "& input": { textAlign: "center", fontSize: "1.3rem", letterSpacing: 2 } }}
          />

          <Button
            fullWidth
            onClick={handleGoToGame}
            sx={{
              mt: 2,
              background: "linear-gradient(45deg, #2196f3, #21cbf3)",
              borderRadius: "25px",
              color: "#fff",
              fontSize: "1.2rem",
              fontWeight: 700,
              py: 1.5,
              animation: `${pulseAnimation} 3s infinite`,
              "&:hover": { background: "linear-gradient(45deg, #1976d2, #2196f3)" },
            }}
          >
            🎮 הצטרף למשחק
          </Button>

          {/* כפתור הורדת אפליקציה */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,.1)' }}>
            <Button
              fullWidth
              component="a"
              href={`${API_URL}/download-app`}
              download
              startIcon={<GetAppIcon />}
              sx={{
                background: 'linear-gradient(45deg, #4caf50, #66bb6a)',
                borderRadius: '25px',
                color: '#fff',
                fontSize: '1rem',
                fontWeight: 700,
                py: 1.2,
                textDecoration: 'none',
                '&:hover': { background: 'linear-gradient(45deg, #388e3c, #4caf50)' },
              }}
            >
              📲 הורד את האפליקציה
            </Button>
            <Typography sx={{ color: 'rgba(255,255,255,.35)', textAlign: 'center', fontSize: '.75rem', mt: 1 }}>
              Android • APK
            </Typography>
          </Box>
        </GameCard>
      </Zoom>
    </MobileContainer>
  )
}

// ============ קומפוננטת המשחק עצמו ============
const MobileGame = ({ gameId }) => {

  // שלבים: join → waiting → playing → result → ended
  const [phase, setPhase] = useState("join")
  const [playerName, setPlayerName] = useState("")
  const [playerPhone, setPlayerPhone] = useState("")
  const [playerId, setPlayerId] = useState(null)
  const [playerIdentifier, setPlayerIdentifier] = useState("")
  const [gameName, setGameName] = useState("")
  const [realGameId, setRealGameId] = useState(gameId)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  // מצב משחק
  const [score, setScore] = useState(0)
  const [questionNum, setQuestionNum] = useState(0)
  const [answered, setAnswered] = useState(false)
  const [lastResult, setLastResult] = useState(null) // { isCorrect, points }
  const [connectedPlayers, setConnectedPlayers] = useState(0)
  const [gameEnded, setGameEnded] = useState(false)
  const [answersLocked, setAnswersLocked] = useState(true) // תשובות נעולות עד שהמסך הראשי מציג הכל
  const [timeUp, setTimeUp] = useState(false) // נגמר הזמן
  const [timerSeconds, setTimerSeconds] = useState(0) // שניות שנותרו
  const [totalTime, setTotalTime] = useState(20) // זמן כולל לשאלה

  const socketRef = useRef(null)
  const pollRef = useRef(null)
  const prevQuestionRef = useRef(0)
  const wasUnlockedRef = useRef(false) // האם התשובות נפתחו כבר לשאלה הזו

  // ============ שחזור סשן מ-localStorage ============
  useEffect(() => {
    try {
      const saved = localStorage.getItem(`mobile_session_${gameId}`);
      if (saved) {
        const s = JSON.parse(saved);
        if (s.playerIdentifier && s.realGameId) {
          setPlayerId(s.playerId);
          setPlayerIdentifier(s.playerIdentifier);
          setGameName(s.gameName);
          setRealGameId(s.realGameId);
          setPlayerName(s.playerName || '');
          setPhase('waiting');
        }
      }
    } catch {}
  }, [gameId]);

  // ============ הצטרפות למשחק ============
  const handleJoin = async () => {
    if (!playerName.trim()) {
      setError("הכנס את שמך")
      return
    }
    setLoading(true)
    setError("")
    try {
      const res = await fetch(`${API_URL}/mobile_join/${gameId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ playerName: playerName.trim(), playerPhone: playerPhone.trim() || undefined }),
      })
      const data = await res.json()
      if (data.status) {
        setPlayerId(data.playerId)
        setPlayerIdentifier(data.playerPhone)
        setGameName(data.gameName)
        setRealGameId(data.gameId)
        setPhase("waiting")
        // שמירת סשן ב-localStorage כדי ששחקן שמרפרש יחזור אוטומטית
        try {
          localStorage.setItem(`mobile_session_${gameId}`, JSON.stringify({
            playerId: data.playerId,
            playerIdentifier: data.playerPhone,
            gameName: data.gameName,
            realGameId: data.gameId,
            playerName: playerName.trim(),
          }));
        } catch {}
      } else {
        setError(data.message || "שגיאה בהצטרפות")
      }
    } catch {
      setError("שגיאה בחיבור לשרת")
    }
    setLoading(false)
  }

  // ============ חיבור WebSocket + Polling ============
  useEffect(() => {
    if (phase !== "waiting" && phase !== "playing") return

    // חיבור סוקט
    const socket = io(WS_URL)
    socketRef.current = socket
    socket.emit("join-game", realGameId)

    socket.on("setCount", (data) => {
      setConnectedPlayers(data.connectedPlayers)
    })

    // Polling כל שנייה לבדיקת שינוי שאלה
    const poll = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL}/mobile_status/${realGameId}?playerPhone=${encodeURIComponent(playerIdentifier)}`)
        const d = await r.json()
        if (!d.status) return
        setConnectedPlayers(d.connectedPlayers)
        setScore(d.playerScore)

        // המשחק באמת נגמר (כל השאלות הסתיימו)
        if (d.gameOver) {
          // שליחת עזיבה אוטומטית כדי שהמונה ירד
          fetch(`${API_URL}/mobile_leave/${realGameId}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ playerPhone: playerIdentifier }),
          }).catch(() => {})
          // מחיקת סשן שמור
          try { localStorage.removeItem(`mobile_session_${gameId}`); } catch {}
          setPhase("ended")
          setGameEnded(true)
          return
        }

        // המשחק עדיין ממתין להתחלה — המנהל בדף ההמתנה
        if (d.waiting) {
          setAnswersLocked(true)
          return
        }

        // עדכון מצב נעילת תשובות (stop = תשובות נעולות, לא סוף משחק)
        if (d.stopped) {
          // אם התשובות היו פתוחות ועכשיו נעולו — נגמר הזמן!
          if (wasUnlockedRef.current) {
            setTimeUp(true)
          }
          setAnswersLocked(true)
          setTimerSeconds(0)
        } else {
          // תשובות פתוחות
          wasUnlockedRef.current = true
          setTimeUp(false)
          setAnswersLocked(false)
          setTotalTime(d.answerTime || 20)
          // חישוב זמן שנותר מהשרת
          if (d.openedAt) {
            const elapsed = Math.floor((Date.now() - new Date(d.openedAt).getTime()) / 1000)
            const remaining = Math.max(0, (d.answerTime || 20) - elapsed)
            setTimerSeconds(remaining)
          }
        }

        if (d.questionNumber > 0 && phase === "waiting") {
          setPhase("playing")
        }

        // שאלה חדשה
        if (d.questionNumber !== prevQuestionRef.current && d.questionNumber > 0) {
          prevQuestionRef.current = d.questionNumber
          setQuestionNum(d.questionNumber)
          setAnswered(d.answeredCurrent)
          setTimeUp(false)
          wasUnlockedRef.current = false
          if (!d.answeredCurrent) {
            setLastResult(null)
          }
        }
      } catch {}
    }, 1000)
    pollRef.current = poll

    return () => {
      socket.disconnect()
      clearInterval(poll)
    }
  }, [phase, realGameId, playerIdentifier])

  // ============ שליחת תשובה ============
  const sendAnswer = async (answerNum) => {
    if (answered || answersLocked) return
    setAnswered(true)

    // ויברציה קצרה (אם נתמך)
    if (navigator.vibrate) navigator.vibrate(50)

    try {
      const r = await fetch(`${API_URL}/mobile_click/${realGameId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answer: String(answerNum), playerPhone: playerIdentifier }),
      })
      const d = await r.json()
      if (d.status) {
        if (d.alreadyAnswered) {
          setLastResult({ isCorrect: null, points: 0, message: "כבר ענית" })
        } else {
          setLastResult({ isCorrect: d.isCorrect, points: d.points || 0 })
          if (d.isCorrect) {
            setScore((prev) => prev + (d.points || 0))
            if (navigator.vibrate) navigator.vibrate([100, 50, 100])
          } else {
            if (navigator.vibrate) navigator.vibrate(200)
          }
        }
      }
    } catch {
      setLastResult({ isCorrect: false, points: 0 })
    }
  }

  // ============ עזיבה ============
  const handleLeave = () => {
    fetch(`${API_URL}/mobile_leave/${realGameId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ playerPhone: playerIdentifier }),
    }).catch(() => {})
    // מחיקת סשן שמור
    try { localStorage.removeItem(`mobile_session_${gameId}`); } catch {}
    setPhase("join")
    setScore(0)
    setQuestionNum(0)
    setAnswered(false)
    setLastResult(null)
  }

  // ============ JOIN SCREEN ============
  if (phase === "join") {
    return (
      <MobileContainer>
        <SparkleEffect />
        <Zoom in timeout={600}>
          <GameCard>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <SportsEsportsIcon sx={{ fontSize: "3.5rem", color: "#2196f3", filter: "drop-shadow(0 0 12px rgba(33,150,243,.7))" }} />
              <Typography
                variant="h4"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  mt: 1,
                  animation: `${glowAnimation} 3s ease-in-out infinite alternate`,
                  background: "linear-gradient(45deg, #2196f3, #21cbf3)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                הצטרפות למשחק
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,.5)", mt: 0.5, fontSize: ".9rem" }}>
                קוד משחק: {gameId}
              </Typography>
            </Box>

            {error && (
              <Fade in>
                <Box sx={{ background: "rgba(244,67,54,.2)", borderRadius: "12px", p: 1.5, mb: 2, textAlign: "center" }}>
                  <Typography sx={{ color: "#ff5252", fontWeight: 600 }}>{error}</Typography>
                </Box>
              </Fade>
            )}

            <StyledInput
              fullWidth
              label="השם שלך"
              variant="outlined"
              margin="normal"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: "rgba(255,255,255,.7)" }} />
                  </InputAdornment>
                ),
              }}
            />

            <StyledInput
              fullWidth
              label="טלפון (אופציונלי)"
              variant="outlined"
              margin="normal"
              value={playerPhone}
              onChange={(e) => setPlayerPhone(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: "rgba(255,255,255,.7)" }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              onClick={handleJoin}
              disabled={loading}
              sx={{
                mt: 2,
                background: "linear-gradient(45deg, #2196f3, #21cbf3)",
                borderRadius: "25px",
                color: "#fff",
                fontSize: "1.2rem",
                fontWeight: 700,
                py: 1.5,
                animation: `${pulseAnimation} 3s infinite`,
                "&:hover": { background: "linear-gradient(45deg, #1976d2, #2196f3)" },
                "&:disabled": { background: "rgba(255,255,255,.3)", animation: "none" },
              }}
            >
              {loading ? <CircularProgress size={28} color="inherit" /> : "🎮 הצטרף למשחק"}
            </Button>
          </GameCard>
        </Zoom>
      </MobileContainer>
    )
  }

  // ============ WAITING SCREEN ============
  if (phase === "waiting") {
    return (
      <MobileContainer>
        <SparkleEffect />
        <Zoom in timeout={600}>
          <GameCard sx={{ textAlign: "center" }}>
            <SportsEsportsIcon
              sx={{
                fontSize: "4rem",
                color: "#2196f3",
                animation: `${floatAnimation} 2s ease-in-out infinite`,
                filter: "drop-shadow(0 0 14px rgba(33,150,243,.6))",
              }}
            />
            <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700, mt: 2 }}>
              {gameName || "משחק"}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,.6)", mt: 1, fontSize: "1.1rem" }}>
              ממתין להתחלת המשחק...
            </Typography>

            <Box sx={{ mt: 3, mb: 2 }}>
              <ScoreChip>
                <EmojiEventsIcon sx={{ fontSize: "1.3rem" }} />
                שחקן #{playerId}
              </ScoreChip>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: "#2196f3",
                    animation: `${sparkleAnimation} 1.5s infinite`,
                    animationDelay: `${i * 0.3}s`,
                  }}
                />
              ))}
            </Box>

            <Typography sx={{ color: "rgba(255,255,255,.4)", mt: 3, fontSize: ".85rem" }}>
              👥 {connectedPlayers} שחקנים מחוברים
            </Typography>

            <Button
              onClick={handleLeave}
              sx={{ mt: 2, color: "rgba(255,255,255,.5)", fontSize: ".85rem" }}
              startIcon={<ExitToAppIcon />}
            >
              יציאה
            </Button>
          </GameCard>
        </Zoom>
      </MobileContainer>
    )
  }

  // ============ PLAYING SCREEN ============
  if (phase === "playing") {
    return (
      <MobileContainer sx={{ justifyContent: "flex-start", pt: 2 }}>
        {/* Header */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "420px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            px: 1,
          }}
        >
          <ScoreChip>
            <EmojiEventsIcon sx={{ fontSize: "1.2rem" }} />
            {score} נק'
          </ScoreChip>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: ".85rem" }}>
              שאלה #{questionNum}
            </Typography>
            <IconButton onClick={handleLeave} sx={{ color: "rgba(255,255,255,.4)" }} size="small">
              <ExitToAppIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Main card */}
        <GameCard>
          {/* Result feedback */}
          {lastResult && (
            <Fade in>
              <Box
                sx={{
                  textAlign: "center",
                  mb: 2,
                  p: 2,
                  borderRadius: "16px",
                  animation: lastResult.isCorrect
                    ? `${correctFlash} 1s ease-in-out`
                    : lastResult.isCorrect === false
                      ? `${wrongFlash} 1s ease-in-out`
                      : "none",
                  background: lastResult.isCorrect
                    ? "rgba(76,175,80,.15)"
                    : lastResult.isCorrect === false
                      ? "rgba(244,67,54,.15)"
                      : "rgba(255,255,255,.05)",
                }}
              >
                {lastResult.isCorrect === true && (
                  <>
                    <CheckCircleIcon sx={{ fontSize: "3rem", color: "#4caf50" }} />
                    <Typography sx={{ color: "#4caf50", fontWeight: 700, fontSize: "1.3rem" }}>
                      נכון! +{lastResult.points} נקודות 🎉
                    </Typography>
                  </>
                )}
                {lastResult.isCorrect === false && (
                  <>
                    <CancelIcon sx={{ fontSize: "3rem", color: "#f44336" }} />
                    <Typography sx={{ color: "#f44336", fontWeight: 700, fontSize: "1.3rem" }}>
                      לא נכון 😔
                    </Typography>
                  </>
                )}
                {lastResult.isCorrect === null && (
                  <Typography sx={{ color: "rgba(255,255,255,.7)", fontWeight: 600 }}>
                    {lastResult.message}
                  </Typography>
                )}
              </Box>
            </Fade>
          )}

          {/* Answer buttons */}
          {answersLocked && !answered ? (
            /* תשובות נעולות */
            <Box sx={{ textAlign: "center", py: 4 }}>
              {timeUp ? (
                /* נגמר הזמן! */
                <>
                  <Typography sx={{ fontSize: "3.5rem", mb: 1 }}>⏰</Typography>
                  <Typography sx={{ color: "#ff9800", fontSize: "1.5rem", fontWeight: 800, mb: 1 }}>
                    נגמר הזמן!!
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: "1rem" }}>
                    המתינו לשאלה הבאה...
                  </Typography>
                </>
              ) : (
                /* ממתין להצגת השאלה על המסך הראשי */
                <>
                  <SportsEsportsIcon
                    sx={{
                      fontSize: "3rem",
                      color: "#2196f3",
                      animation: `${floatAnimation} 2s ease-in-out infinite`,
                      opacity: 0.7,
                    }}
                  />
                  <Typography sx={{ color: "rgba(255,255,255,.6)", mt: 2, fontSize: "1.1rem", fontWeight: 600 }}>
                    📺 שאלה #{questionNum}
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,.4)", mt: 1, fontSize: ".95rem" }}>
                    הסתכלו על המסך... התשובות ייפתחו בקרוב
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#2196f3",
                          animation: `${sparkleAnimation} 1.5s infinite`,
                          animationDelay: `${i * 0.3}s`,
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          ) : !answered ? (
            <Box sx={{ mt: 1 }}>
              {/* טיימר */}
              {timerSeconds > 0 && (
                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <Box sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    background: timerSeconds <= 5 ? "rgba(244,67,54,.25)" : "rgba(33,150,243,.15)",
                    border: `2px solid ${timerSeconds <= 5 ? "rgba(244,67,54,.5)" : "rgba(33,150,243,.3)"}`,
                    borderRadius: "50px",
                    px: 2.5,
                    py: 0.8,
                    transition: "all .3s ease",
                  }}>
                    <Typography sx={{ fontSize: "1.2rem" }}>⏱️</Typography>
                    <Typography sx={{
                      color: timerSeconds <= 5 ? "#ff5252" : "#fff",
                      fontWeight: 800,
                      fontSize: "1.4rem",
                      fontFamily: "monospace",
                      minWidth: "2ch",
                      textAlign: "center",
                    }}>
                      {timerSeconds}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Typography
                sx={{
                  color: "rgba(255,255,255,.6)",
                  textAlign: "center",
                  mb: 2,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                }}
              >
                בחר תשובה:
              </Typography>
              {[1, 2, 3].map((num) => (
                <AnswerButton
                  key={num}
                  onClick={() => sendAnswer(num)}
                  sx={{
                    animation: `${floatAnimation} ${2 + num * 0.3}s ease-in-out infinite`,
                  }}
                >
                  {num === 1 ? "1️⃣" : num === 2 ? "2️⃣" : "3️⃣"} תשובה {num}
                </AnswerButton>
              ))}
            </Box>
          ) : !lastResult ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress size={40} sx={{ color: "#2196f3" }} />
              <Typography sx={{ color: "rgba(255,255,255,.6)", mt: 2 }}>
                שולח תשובה...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: "1rem" }}>
                ⏳ ממתין לשאלה הבאה...
              </Typography>
            </Box>
          )}
        </GameCard>

        {/* Connected players */}
        <Typography sx={{ color: "rgba(255,255,255,.3)", mt: 2, fontSize: ".8rem" }}>
          👥 {connectedPlayers} שחקנים | 🏆 ניקוד: {score}
        </Typography>
      </MobileContainer>
    )
  }

  // ============ ENDED SCREEN ============
  if (phase === "ended") {
    return (
      <MobileContainer>
        <SparkleEffect />
        <Zoom in timeout={600}>
          <GameCard sx={{ textAlign: "center" }}>
            <EmojiEventsIcon
              sx={{
                fontSize: "5rem",
                color: "#ffd700",
                animation: `${floatAnimation} 2s ease-in-out infinite`,
                filter: "drop-shadow(0 0 20px rgba(255,215,0,.6))",
              }}
            />
            <Typography
              variant="h4"
              sx={{
                color: "#fff",
                fontWeight: 700,
                mt: 2,
                background: "linear-gradient(45deg, #ffd700, #ff9800)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              המשחק הסתיים!
            </Typography>

            <Box sx={{ mt: 3 }}>
              <ScoreChip sx={{ fontSize: "1.4rem", py: 1.5, px: 4 }}>
                <StarIcon sx={{ fontSize: "1.5rem" }} />
                {score} נקודות
              </ScoreChip>
            </Box>

            <Typography sx={{ color: "rgba(255,255,255,.5)", mt: 3 }}>
              תודה ששיחקת! 🎮
            </Typography>

            <Button
              fullWidth
              onClick={() => {
                try { localStorage.removeItem(`mobile_session_${gameId}`); } catch {}
                setPhase("join")
                setScore(0)
                setQuestionNum(0)
                setAnswered(false)
                setLastResult(null)
              }}
              sx={{
                mt: 3,
                background: "linear-gradient(45deg, #2196f3, #21cbf3)",
                borderRadius: "25px",
                color: "#fff",
                fontSize: "1.1rem",
                fontWeight: 700,
                py: 1.2,
                "&:hover": { background: "linear-gradient(45deg, #1976d2, #2196f3)" },
              }}
            >
              🔄 שחק שוב
            </Button>
          </GameCard>
        </Zoom>
      </MobileContainer>
    )
  }

  return null
}

export default MobilePlay
